import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share, PlusSquare } from "lucide-react";

const DISMISS_KEY = "pwa_install_dismissed_at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // re-ask after 7 days

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const recentlyDismissed = () => {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return ts && Date.now() - ts < SNOOZE_MS;
};

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — show manual instructions instead.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => { setIosHint(true); setVisible(true); }, 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (_) {}
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-5 pointer-events-none"
      data-testid="pwa-install-banner"
    >
      <div className="pointer-events-auto max-w-md mx-auto bg-white border border-border rounded-sm shadow-lg overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <img src="/logo-mark.png" alt="BookdHUB" className="w-11 h-11 rounded-sm shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-semibold text-base leading-tight">Install BookdHUB</p>
            {iosHint ? (
              <p className="text-sm text-muted-foreground mt-1">
                Tap <Share className="inline w-4 h-4 mx-0.5 -mt-0.5" /> then{" "}
                <span className="whitespace-nowrap"><PlusSquare className="inline w-4 h-4 mx-0.5 -mt-0.5" /> Add to Home Screen</span>{" "}
                for one-tap booking.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                Add it to your home screen for faster, app-like booking — no store needed.
              </p>
            )}
          </div>
          <button
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground shrink-0 -mr-1 -mt-1 p-1"
            aria-label="Dismiss"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {!iosHint && (
          <div className="flex gap-2 px-4 pb-4">
            <Button className="rounded-sm flex-1" onClick={install} data-testid="pwa-install-btn">
              <Download className="w-4 h-4 mr-1.5" />Install app
            </Button>
            <Button variant="ghost" className="rounded-sm" onClick={dismiss} data-testid="pwa-install-later">
              Not now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
